import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { FileCheck, Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const CartoesPontoPage = () => {
  const { api, user } = useAuth();
  const [cartoes, setCartoes] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedEmpregado, setSelectedEmpregado] = useState('');
  const [selectedMes, setSelectedMes] = useState(String(new Date().getMonth() + 1));
  const [selectedAno, setSelectedAno] = useState(String(new Date().getFullYear()));

  const meses = [
    { value: '1', label: 'Janeiro' },
    { value: '2', label: 'Fevereiro' },
    { value: '3', label: 'Março' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Maio' },
    { value: '6', label: 'Junho' },
    { value: '7', label: 'Julho' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];

  const anos = ['2024', '2025', '2026'];

  useEffect(() => {
    fetchCartoes();
    fetchEmpregados();
  }, []);

  const fetchCartoes = async () => {
    try {
      const response = await api.get('/cartoes-ponto');
      setCartoes(response.data);
    } catch (error) {
      console.error('Error fetching cartoes:', error);
      toast.error('Erro ao carregar cartões de ponto');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data.filter(e => e.ativo));
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const gerarCartao = async () => {
    if (!selectedEmpregado) {
      toast.error('Selecione um empregado');
      return;
    }

    setGenerating(true);
    try {
      await api.post(`/cartoes-ponto/gerar?empresa_id=${user?.empresa_id}&empregado_id=${selectedEmpregado}&mes=${selectedMes}&ano=${selectedAno}`);
      toast.success('Cartão de ponto gerado!');
      fetchCartoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar cartão');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCartao = async (cartaoId) => {
    try {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/cartoes-ponto/download/${cartaoId}`, '_blank');
    } catch (error) {
      toast.error('Erro ao baixar cartão');
    }
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'N/A';
  };

  const getMesLabel = (mes) => {
    const found = meses.find(m => m.value === String(mes));
    return found?.label || mes;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cartoes-ponto-page">
      <div>
        <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Cartões de Ponto</h2>
        <p className="text-muted-foreground">Geração e download de cartões de ponto mensais</p>
      </div>

      {/* Generate Card */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Gerar Cartão de Ponto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Empregado</label>
              <Select value={selectedEmpregado} onValueChange={setSelectedEmpregado}>
                <SelectTrigger data-testid="select-empregado">
                  <SelectValue placeholder="Selecione o empregado" />
                </SelectTrigger>
                <SelectContent>
                  {empregados.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.matricula} - {emp.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32 space-y-2">
              <label className="text-sm font-medium">Mês</label>
              <Select value={selectedMes} onValueChange={setSelectedMes}>
                <SelectTrigger data-testid="select-mes">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map(mes => (
                    <SelectItem key={mes.value} value={mes.value}>
                      {mes.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-28 space-y-2">
              <label className="text-sm font-medium">Ano</label>
              <Select value={selectedAno} onValueChange={setSelectedAno}>
                <SelectTrigger data-testid="select-ano">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anos.map(ano => (
                    <SelectItem key={ano} value={ano}>
                      {ano}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={gerarCartao}
              disabled={generating}
              className="bg-accent hover:bg-accent/90"
              data-testid="gerar-cartao-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Gerando...' : 'Gerar PDF'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Cartões Gerados</CardTitle>
        </CardHeader>
        <CardContent>
          {cartoes.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum cartão de ponto gerado</p>
              <p className="text-sm text-muted-foreground mt-1">
                Selecione um empregado e período para gerar
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empregado</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Gerado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cartoes.map((cartao) => (
                    <TableRow key={cartao.id} data-testid={`cartao-row-${cartao.id}`}>
                      <TableCell className="font-medium">{getEmpregadoNome(cartao.empregado_id)}</TableCell>
                      <TableCell>{getMesLabel(cartao.mes)}/{cartao.ano}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(cartao.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => downloadCartao(cartao.id)}
                          data-testid={`download-${cartao.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Baixar PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CartoesPontoPage;
